import { ImgID } from "../../image_loader/img_id.js";
import type { ClaimFreeOfferResponse, OfferDto } from "../../api/dto/offers.dto.js";
import type { PlayerDto } from "../../api/dto/player.dto.js";
import { OfferService } from "../../offers/offer_service.js";
import { isPurchaseErrorReported } from "../../offers/purchase_failure.js";
import { IapPlugin } from "../../plugins/iap_plugin.js";
import {
  getMysteryBoxPossibleRewards,
  MYSTERY_BOX_AD_PLACEMENT,
  MysteryBoxPossibleReward,
  mysteryBoxAnalyticsContext,
  mysteryBoxGrantedRewards,
  mysteryBoxIconSize,
  mysteryBoxRangeText,
  mysteryBoxRewardIcon,
  rollMysteryBoxPreviewRewards,
  mysteryBoxMode,
  type MysteryBoxMode,
} from "../../offers/mystery_box_offer.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../api/player_sync.js";
import { showRewarded } from "../../ads/rewarded.js";
import { areAdsBlockedOffline } from "../../ads/offline_ads.js";
import { formatShortDuration } from "../../common/common_funcs.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { Game } from "../../game.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { setButtonTextOutline } from "../common_ui_elements/button_text_outline.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AnchoredTooltip } from "../tooltip/anchored_tooltip.js";
import { hideOfflineAdTooltip, showAdUnavailableTooltip, showOfflineAdTooltip } from "../tooltip/offline_ad_tooltip.js";
import { RewardTooltip } from "../tooltip/reward_tooltip.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { crashCapture } from "../../crash/crash_reporter.js";
import {
  isRewardedCooldownActive,
  rewardedCooldownRemainingMs,
  rewardedCooldownTimerText,
  startRewardedCooldown,
} from "../../ads/rewarded_booster_cooldown.js";

/** Keeps press feedback but lets DialogManager clear its transparent canvas before the repaint. */
class DialogSafePressImage extends UIImage
{
  public override forceRenderParent(): void
  {
    this.forceMakeShouldBeRendered();
  }
}

/**
 * Rewarded offer: the player watches an ad, the server rolls one of the
 * possible rewards and the MysteryBoxOverlay opens the chest with the result.
 *
 * Laid out from two Figma frames (1080x1920 and 1080x2340); every vertical
 * coordinate is lerped between them with LayoutScaler.*FromLayouts, so the
 * art grows into the extra height of tall phones instead of leaving a gap.
 * `offer === null` is a debug preview: no ad, the roll happens locally.
 */
export class MysteryBoxDialog extends AbstractDialog
{
  private static readonly TOOLTIP_OWNER = 'mystery_box';
  /** tooltip_edge.png box (design px) and how far it overlaps the bubble's frame. */
  private static readonly TOOLTIP_TAIL_W = 111;
  private static readonly TOOLTIP_TAIL_H = 78;
  private static readonly TOOLTIP_TAIL_Y_OFFSET = -32;
  /** Top of the rewards bubble's frame in the 1920 frame (the nose sits above it). */
  private static readonly PANEL_BODY_Y = 1028;

  private readonly design_x: number;
  private readonly design_y: number;
  private readonly possible_rewards: MysteryBoxPossibleReward[];
  private readonly placement_id: string;
  private readonly trigger_event_id: string | undefined;

  private watch_button!: UINineSliceElement;
  private watch_active_content!: UIElementHolder;
  private reward_cooldown_content: UIElementHolder | null = null;
  private reward_cooldown_text: UITextElement | null = null;
  private reward_cooldown_visible = false;
  private shown_reward_cooldown_timer = "";
  private timer_text?: UITextElement;
  private timer_end_ms: number | null;
  private next_timer_update_at = 0;
  private next_cooldown_update_at = 0;
  private watch_inflight = false;
  private shown_inactive: boolean | null = null;
  private close_tracked = false;
  private readonly mode: MysteryBoxMode;
  private button_text?: UITextElement;

  constructor(
    private readonly offer: OfferDto | null,
    context: { placement_id: string; trigger_event_id?: string },
  )
  {
    super( DialogManager.ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), false );

    this.placement_id = context.placement_id;
    this.trigger_event_id = context.trigger_event_id;
    this.mode = mysteryBoxMode( offer );
    this.possible_rewards = getMysteryBoxPossibleRewards( offer ).slice( 0, 4 );
    // The preview shows the Figma countdown so the whole layout can be checked.
    this.timer_end_ms = offer ? OfferService.getOfferDisplayEndMs( offer ) : Date.now() + 47 * 3600_000;

    // Figma frames are 1080 wide; on wider screens the layout stays centered,
    // and on screens taller than the 2340 frame the whole block is centered.
    this.design_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) ) / 2;
    this.design_y = ( this.rect.getH() - LayoutScaler.heightFromLayouts( 1920, 2340 ) ) / 2;

    this.createBackground();
    this.createArtwork();
    this.createTitle();
    this.createTimer();
    this.createRewardsPanel();
    this.createWatchText();
    this.createWatchButton();
    this.createInfoButton();
    this.createCloseButton();
    this.syncWatchButton( performance.now(), true );
  }

  public name(): string { return 'MysteryBoxDialog'; }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.updateTimer( now );
    this.syncWatchButton( now );
  }

  public override onDialogShown(): void
  {
    if ( !this.offer ) return;
    void OfferService.trackShown( this.offer.id, this.analyticsContext() );
  }

  public override onDialogCloseRequested( reason: string ): void
  {
    if ( !this.offer || this.close_tracked ) return;
    this.close_tracked = true;
    void OfferService.trackClosed( this.offer.id, { ...this.analyticsContext(), reason } );
  }

  public override onDialogWillClose(): void
  {
    hideOfflineAdTooltip();
    AnchoredTooltip.hide( MysteryBoxDialog.TOOLTIP_OWNER );
  }

  public override cleanup(): void
  {
    if ( this.watch_button && this.watch_active_content && this.reward_cooldown_content )
    {
      this.watch_button.removeChild( this.watch_active_content );
      this.watch_button.removeChild( this.reward_cooldown_content );
      this.watch_active_content.cleanup();
      this.reward_cooldown_content.cleanup();
    }
    super.cleanup();
  }

  // ---- layout helpers: design px -> dialog px ----

  private x( design_x: number ): number
  {
    return this.design_x + LayoutScaler.xFromDesign( design_x );
  }

  /** Vertical position lerped between the 1920 and 2340 frames. */
  private y( y_1920: number, y_2340: number ): number
  {
    return this.design_y + LayoutScaler.yFromLayouts( y_1920, y_2340 );
  }

  private createBackground(): void
  {
    const radius = LayoutScaler.cornerRadiusFromDesign( 100 );
    const top = this.y( 331, 751 );
    const bottom = this.y( 1812, 2205 );
    this.addChild( new RoundedRectangle(
      this.ctx,
      this.x( 23 ),
      top,
      LayoutScaler.widthFromDesign( 1037 ),
      bottom - top,
      { tl: 0, tr: 0, br: radius, bl: radius },
      {
        type: 'linear-gradient',
        direction: 'vertical',
        stops: [
          { offset: 0, color: 'rgba(8, 64, 207, 0)' },
          { offset: 1, color: 'rgba(8, 64, 208, 1)' },
        ],
      },
    ) );
  }

  private createArtwork(): void
  {
    // The PNG already carries the blue glow disc and the floor shadow; the
    // disc is centered at x=553 in both frames.
    const size = LayoutScaler.widthFromHeightLayouts( 804.7, 1106 );
    this.addChild( new UIImage( this.ctx, this.x( 553 ) - size / 2, this.y( 331, 385.5 ), size, size, ImgID.UI_MysteryBoxTiger ) );
  }

  private createTitle(): void
  {
    this.addChild( new UIImage(
      this.ctx,
      this.x( 104 ),
      this.y( 58, 115 ),
      LayoutScaler.widthFromDesign( 870 ),
      LayoutScaler.heightFromDesign( 321 ),
      ImgID.UI_MysteryBoxTitle,
    ) );
  }

  private createTimer(): void
  {
    if ( this.timer_end_ms === null ) return;

    const w = LayoutScaler.widthFromDesign( 288 );
    const h = LayoutScaler.heightFromDesign( 78 );
    const timer = new UIImage( this.ctx, this.x( 410 ), this.y( 316, 371 ), w, h, ImgID.UI_NoAdsTimer );
    const text = new UITextElement( this.ctx, LayoutScaler.xFromDesign( 72 ), 0, w - LayoutScaler.widthFromDesign( 86 ), h, {
      text: '',
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: '#FFFFFF',
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      center_on_cap_height: true,
      ...TextStyleScheme.timerShadow(),
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
    } );
    timer.addChild( text, true );
    this.addChild( timer );
    this.timer_text = text;
    this.updateTimer( performance.now(), true );
  }

  private updateTimer( now: number, force = false ): void
  {
    if ( !this.timer_text || this.timer_end_ms === null ) return;
    if ( !force && now < this.next_timer_update_at ) return;
    this.next_timer_update_at = now + 1000;

    const remaining_ms = Math.max( 0, this.timer_end_ms - ( OfferService.getServerNowMs() ?? Date.now() ) );
    const text = formatShortDuration( remaining_ms );
    if ( text === this.timer_text.getText() ) return;
    this.timer_text.setText( text );
    this.invalidateSubtree();
  }

  private createRewardsPanel(): void
  {
    // Same bubble as the info tooltip: the tooltip frame and its nose pointing at the chest.
    this.addChild( new UINineSliceElement(
      this.ctx,
      this.x( 32 ),
      this.y( MysteryBoxDialog.PANEL_BODY_Y, MysteryBoxDialog.PANEL_BODY_Y + 369 ),
      LayoutScaler.widthFromDesign( 1017 ),
      LayoutScaler.heightFromDesign( 1410 - MysteryBoxDialog.PANEL_BODY_Y ),
      ImgID.UI_TutorialSubstrate,
      'tutorial_substrate',
    ) );

    const nose_y = MysteryBoxDialog.PANEL_BODY_Y - MysteryBoxDialog.TOOLTIP_TAIL_H - MysteryBoxDialog.TOOLTIP_TAIL_Y_OFFSET;
    this.addChild( new UIImage(
      this.ctx,
      this.x( 540 - MysteryBoxDialog.TOOLTIP_TAIL_W / 2 ),
      this.y( nose_y, nose_y + 369 ),
      LayoutScaler.widthFromDesign( MysteryBoxDialog.TOOLTIP_TAIL_W ),
      LayoutScaler.heightFromDesign( MysteryBoxDialog.TOOLTIP_TAIL_H ),
      ImgID.UI_MysteryBoxTooltipEdge,
    ) );

    this.addChild( this.createOutlinedText( 'MYSTERY_BOX.POSSIBLE_REWARDS', 'Possible rewards:', this.y( 1064, 1433 ), 64, 10 ) );

    const count = this.possible_rewards.length;
    const pitch = 235;
    const cell_w = 224;
    const row_w = count * cell_w + ( count - 1 ) * ( pitch - cell_w );
    const first_x = 540 - row_w / 2;
    this.possible_rewards.forEach( ( reward, index ) => this.addPossibleReward( reward, first_x + index * pitch ) );
  }

  private addPossibleReward( reward: MysteryBoxPossibleReward, design_x: number ): void
  {
    const cell_w = LayoutScaler.widthFromDesign( 224 );
    const cell_h = LayoutScaler.heightFromDesign( 216 );
    const cell = new RoundedRectangle(
      this.ctx,
      this.x( design_x ),
      this.y( 1143, 1512 ),
      cell_w,
      cell_h,
      LayoutScaler.cornerRadiusFromDesign( 23 ),
      '#E1CCC2',
      undefined,
      false,
      undefined,
      0,
      undefined,
      { color: 'rgba(103, 17, 17, 0.16)', blur: LayoutScaler.widthFromDesign( 6 ), offsetY: LayoutScaler.yFromDesign( 3 ) },
    );

    const slot = LayoutScaler.widthFromDesign( 196 );
    const icon_size = mysteryBoxIconSize( reward.type, slot );
    const icon = new UIImage(
      this.ctx,
      ( cell_w - icon_size.w ) / 2,
      ( slot - icon_size.h ) / 2,
      icon_size.w,
      icon_size.h,
      mysteryBoxRewardIcon( reward.type ),
    );
    cell.addChild( icon, true );

    const label = new UITextElement( this.ctx, 0, LayoutScaler.yFromDesign( 148 ), cell_w, LayoutScaler.heightFromDesign( 64 ), {
      text: mysteryBoxRangeText( reward ),
      font_size: LayoutScaler.textSizeFromDesign( 50 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: '#FFFFFF',
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: '#003F87',
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
    } );
    cell.addChild( label, true );

    this.addChild( cell );
    RewardTooltip.attach( cell, reward.type );
  }

  private createWatchText(): void
  {
    const [ key, fallback ] = this.mode === 'free'
      ? [ 'MYSTERY_BOX.CLAIM_TEXT', 'Open the box and get rewarded!' ]
      : this.mode === 'paid'
        ? [ 'MYSTERY_BOX.BUY_TEXT', 'Buy the box and get rewarded!' ]
        : [ 'MYSTERY_BOX.WATCH_TEXT', 'Watch the ad and get rewarded!' ];
    this.addChild( this.createOutlinedText( key, fallback, this.y( 1444, 1813 ), 66, 8 ) );
  }

  private createOutlinedText( key: string, fallback: string, y: number, design_h: number, design_outline: number ): UITextElement
  {
    const localized = LocalizationManager.t( key );
    return new UITextElement( this.ctx, this.x( 70 ), y, LayoutScaler.widthFromDesign( 940 ), LayoutScaler.heightFromDesign( design_h ), {
      text: localized === key ? fallback : localized,
      font_size: LayoutScaler.textSizeFromDesign( 55 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: '#FFFFFF',
      outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline ),
      outline_color: '#20118D',
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 36 ),
    } );
  }

  private createWatchButton(): void
  {
    const w = LayoutScaler.widthFromDesign( 642 );
    const h = LayoutScaler.heightFromDesign( 250 );
    const button = new UINineSliceElement( this.ctx, this.x( 219 ), this.y( 1549, 1918 ), w, h, ImgID.UI_PurpleFrameBtn, 'frame_btn', undefined, undefined, true );

    const content = new UIElementHolder( this.ctx, 0, 0, w, h );
    const with_icon = this.mode === 'rewarded';
    if ( with_icon )
      content.addChild( new UIImage(
        this.ctx,
        LayoutScaler.xFromDesign( 105 ),
        LayoutScaler.yFromDesign( 78 ),
        LayoutScaler.widthFromDesign( 104 ),
        LayoutScaler.heightFromDesign( 93 ),
        ImgID.UI_VideoIcon,
      ), true );
    const text_x = with_icon ? LayoutScaler.xFromDesign( 226 ) : LayoutScaler.xFromDesign( 60 );
    const text_w = with_icon ? LayoutScaler.widthFromDesign( 320 ) : w - LayoutScaler.widthFromDesign( 120 );
    const text_y = with_icon ? LayoutScaler.heightFromDesign( 8 ) : 0;
    const button_text = new UITextElement( this.ctx, text_x, text_y, text_w, h - LayoutScaler.heightFromDesign( 8 ), {
      ...( this.mode === 'rewarded' ? { localization_key: 'MYSTERY_BOX.WATCH_BUTTON' }
        : this.mode === 'free' ? { localization_key: 'MYSTERY_BOX.CLAIM_BUTTON' }
        : { text: '...' } ),
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: '#FFFFFF',
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 36 ),
      outline_color: ColorScheme.more_lives_purple_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      ...TextStyleScheme.buttonShadow(),
    } );
    content.addChild( button_text, true );
    button.addChild( content, true );

    if ( this.mode === 'rewarded' )
    {
      const clock_size = LayoutScaler.widthFromDesign( 104 );
      const clock = new UIImage(
        this.ctx,
        LayoutScaler.xFromDesign( 105 ),
        ( h - clock_size ) / 2,
        clock_size,
        clock_size,
        ImgID.UI_ClockFrame,
      );
      const hand_h = LayoutScaler.heightFromDesign( 80 );
      const hand_w = hand_h * ( 24 / 73 );
      const hand = new UIImage(
        this.ctx,
        ( clock_size - hand_w ) / 2,
        ( clock_size - hand_h ) / 2,
        hand_w,
        hand_h,
        ImgID.UI_ClockHand,
      );
      hand.enableRotation();
      hand.setRotationImmediate( Math.PI / 6 );
      clock.addChild( hand );

      const cooldown_text = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 226 ),
        LayoutScaler.yFromDesign( 75 ),
        LayoutScaler.widthFromDesign( 320 ),
        LayoutScaler.heightFromDesign( 100 ),
        {
          text: '00:00:00',
          font_size: LayoutScaler.textSizeFromDesign( 60 ),
          font_weight: 900,
          text_style: TextStyleScheme.geologica,
          text_color: '#FFFFFF',
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          auto_wrap: false,
          scale_to_width: true,
          ...TextStyleScheme.buttonShadow(),
        },
      );
      const cooldown_content = new UIElementHolder( this.ctx, 0, 0, w, h );
      cooldown_content.addChild( clock, true );
      cooldown_content.addChild( cooldown_text, true );
      cooldown_content.setClickable( false );
      this.reward_cooldown_content = cooldown_content;
      this.reward_cooldown_text = cooldown_text;
      setButtonTextOutline( [ cooldown_text ], false, ColorScheme.more_lives_purple_outline );
    }

    button.setOnClick( () => void this.watch() );
    this.addChild( button );
    this.watch_button = button;
    this.watch_active_content = content;
    this.button_text = button_text;
    if ( this.mode === 'paid' ) void this.loadPrice();
  }

  private async loadPrice(): Promise<void>
  {
    const offer = this.offer;
    const product_id = offer ? OfferService.getPlatformProductId( offer.id ) : null;
    const context = this.analyticsContext();
    if ( !offer || !product_id )
    {
      this.button_text?.setText( 'BUY' );
      return;
    }
    try
    {
      const details = await IapPlugin.getProductDetails( product_id );
      this.button_text?.setText( details?.formatted_price ?? 'BUY' );
      if ( details?.formatted_price ) await OfferService.trackPriceLoaded( offer.id, { ...context, productid: product_id } );
      else await OfferService.trackPriceUnavailable( offer.id, { ...context, productid: product_id } );
    }
    catch
    {
      this.button_text?.setText( 'BUY' );
      await OfferService.trackPriceUnavailable( offer.id, { ...context, productid: product_id } );
    }
  }

  /** Offline the ad cannot play: the button turns grey and a tap explains why (product decision, see offline_ads.ts). */
  private syncWatchButton( now: number, force = false ): void
  {
    if ( !force && now < this.next_cooldown_update_at ) return;
    this.next_cooldown_update_at = now + 250;

    const uses_cooldown = this.offer !== null && this.mode === 'rewarded';
    const cooldown_remaining = uses_cooldown ? rewardedCooldownRemainingMs( 'mysterybox' ) : 0;
    const cooldown_active = cooldown_remaining > 0;
    const timer_text = rewardedCooldownTimerText( cooldown_remaining );

    if ( timer_text !== this.shown_reward_cooldown_timer )
    {
      this.shown_reward_cooldown_timer = timer_text;
      this.reward_cooldown_text?.setText( timer_text );
      this.invalidateSubtree();
    }

    if ( this.reward_cooldown_content && cooldown_active !== this.reward_cooldown_visible )
    {
      this.reward_cooldown_visible = cooldown_active;
      if ( cooldown_active )
      {
        this.watch_button.removeChild( this.watch_active_content );
        this.watch_button.addChild( this.reward_cooldown_content, true );
      }
      else
      {
        this.watch_button.removeChild( this.reward_cooldown_content );
        this.watch_button.addChild( this.watch_active_content, true );
      }
      this.invalidateSubtree();
    }

    const blocked = this.offer !== null && this.mode === 'rewarded' && areAdsBlockedOffline();
    const inactive = blocked || cooldown_active || this.watch_inflight;
    // Offline remains clickable so the explanatory tooltip can open.
    this.watch_button.setClickable( !cooldown_active && !this.watch_inflight );
    if ( inactive === this.shown_inactive ) return;
    this.shown_inactive = inactive;
    this.watch_button.setImgId( inactive ? ImgID.UI_NonActiveBtn : ImgID.UI_PurpleFrameBtn, inactive ? 'non_active_btn' : 'frame_btn' );
    setButtonTextOutline( [ this.button_text ], !inactive, ColorScheme.more_lives_purple_outline );
    if ( !blocked ) hideOfflineAdTooltip();
    this.invalidateSubtree();
  }

  private createInfoButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 147 );
    const button = new DialogSafePressImage( this.ctx, this.x( 20 ), this.y( 87, 141 ), size, size, ImgID.UI_MysteryBoxInfoBtn, {}, true );
    button.setOnClick( () =>
    {
      const key = this.mode === 'free' ? 'MYSTERY_BOX.INFO_FREE' : this.mode === 'paid' ? 'MYSTERY_BOX.INFO_PAID' : 'MYSTERY_BOX.INFO';
      const text = LocalizationManager.t( key );
      const fallback = this.mode === 'free'
        ? 'Open the Mystery Box and get a random reward!'
        : this.mode === 'paid'
          ? 'Buy the Mystery Box and get a random reward!'
          : 'Watch an ad to open the Mystery Box and get a random reward!';
      AnchoredTooltip.show( button, {
        text: '',
        anchor_touch_closes: true,
        custom: {
          design_w: 770,
          design_h: 330,
          tail_x: 80,
          tail_img: ImgID.UI_MysteryBoxTooltipEdge,
          tail_y_offset: MysteryBoxDialog.TOOLTIP_TAIL_Y_OFFSET,
          build: ( ctx, substrate, w, h ) => substrate.addChild( this.createInfoText( ctx, text === key ? fallback : text, w, h ), true ),
        },
      }, MysteryBoxDialog.TOOLTIP_OWNER );
    } );
    this.addChild( button );
  }

  private createInfoText( ctx: CanvasRenderingContext2D, text: string, w: number, h: number ): UITextElement
  {
    const pad_x = LayoutScaler.widthFromDesign( 55 );
    const pad_y = LayoutScaler.heightFromDesign( 38 );
    return new UITextElement( ctx, pad_x, pad_y, w - pad_x * 2, h - pad_y * 2, {
      text,
      font_size: LayoutScaler.textSizeFromDesign( 46 ),
      font_weight: 900,
      text_color: '#2F4B87',
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
      outline_color: ColorScheme.white,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 28 ),
      line_height: 1.08,
      auto_wrap: true,
    } );
  }

  private createCloseButton(): void
  {
    const size = LayoutScaler.widthFromDesign( 110 );
    const button = new DialogSafePressImage( this.ctx, this.x( 948 ), this.y( 104, 159 ), size, size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    button.setOnClick( () => this.close( 'dismissed' ) );
    this.addChild( button );
  }

  private analyticsContext(): Record<string, unknown>
  {
    return mysteryBoxAnalyticsContext( this.placement_id, this.trigger_event_id );
  }

  private async watch(): Promise<void>
  {
    if ( this.watch_inflight
      || ( this.offer !== null && this.mode === 'rewarded' && isRewardedCooldownActive( 'mysterybox' ) ) )
      return;

    if ( !this.offer )
    {
      this.openRewards( rollMysteryBoxPreviewRewards( this.possible_rewards ), StatisticsStorage.load() );
      return;
    }

    if ( this.mode === 'rewarded' && areAdsBlockedOffline() )
    {
      showOfflineAdTooltip( this.watch_button );
      return;
    }

    const offer = this.offer;
    const context = this.analyticsContext();
    this.watch_inflight = true;
    this.syncWatchButton( performance.now(), true );

    try
    {
      void OfferService.trackClicked( offer.id, context );
      const stats_before = StatisticsStorage.load();
      let result: { player?: PlayerDto | null; granted?: ClaimFreeOfferResponse['granted'] };

      if ( this.mode === 'paid' )
      {
        result = await OfferService.purchaseOffer( offer.id, context );
      }
      else if ( this.mode === 'free' )
      {
        result = await OfferService.claimFreeOffer( offer.id, context );
      }
      else
      {
        const watched = await showRewarded( MYSTERY_BOX_AD_PLACEMENT );
        await OfferService.trackRewardedAdOutcome( offer.id, watched, context );
        if ( !watched.rewarded )
        {
          if ( !watched.shown ) showAdUnavailableTooltip( this.watch_button );
          return;
        }
        result = await OfferService.claimFreeOffer(
          offer.id,
          { ...context, rewarded: true, rewarded_ad_id: watched.ad_id },
          { ad_id: watched.ad_id },
        );
      }
      if ( result.player ) adoptEconomyFromPlayer( result.player );
      else await refreshPlayerFromServer();

      if ( this.mode === 'rewarded' )
        startRewardedCooldown( 'mysterybox' );

      this.openRewards( mysteryBoxGrantedRewards( result.granted ), stats_before );
    }
    catch ( error )
    {
      console.warn( '[MysteryBox] claim failed', error );
      if ( !isPurchaseErrorReported( error ) )
        crashCapture( error, { phase: 'mystery_box_claim', offer_id: offer.id } );
    }
    finally
    {
      this.watch_inflight = false;
      this.syncWatchButton( performance.now(), true );
    }
  }

  private openRewards( rewards: ReturnType<typeof mysteryBoxGrantedRewards>, stats_before: ReturnType<typeof StatisticsStorage.load> ): void
  {
    this.close( 'purchase_success' );
    if ( !rewards.length ) return;
    OverlayFactory.openOverlay( OverlayId.MYSTERY_BOX_OVERLAY, { rewards, stats_before } );
  }
}
